import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, CheckCircle, Hand, Loader2, Megaphone, MessageCircle, UserRound } from "lucide-react";
import EtapasPedido from "./EtapasPedido";
import FilaAtendimento from "./FilaAtendimento";
import { ItemIA, ItemNumero, ItemTexto } from "./Itens";
import { Entretenimento, PausaAtendente, useBotSettings } from "./MensagensBot";
import ModelosOficiais from "./ModelosOficiais";
import Modulo from "./Modulo";
import PreviaCelular from "./PreviaCelular";
import { cleoApi } from "./api";
import { CarregandoBloco, CarregandoFormulario } from "../../components/carregando";

const API = process.env.REACT_APP_BACKEND_URL || "";
const ICONES = { recepcao: Hand, atendimento: MessageCircle, avisos_pedido: Bell, atendimento_humano: UserRound, pos_venda: Megaphone };
// Item do schema que liga/desliga o módulo inteiro (vai no cabeçalho). Cada
// módulo tem a sua chave e uma não depende da outra.
const CHAVE_DO_MODULO = { recepcao: "ativa", atendimento: "ativo", avisos_pedido: "ativo", atendimento_humano: "ativo" };
// O que acontece com o módulo desligado (aviso no topo do módulo aberto).
const DESLIGADO = {
  atendimento: "Desligado: depois da recepção a CLEO fica em silêncio e a equipe atende. O \"Acompanhar pedido\" continua respondendo (é aviso do pedido).",
  avisos_pedido: "Desligado: a CLEO não manda nenhum aviso de pedido — etapas, pagamento, PIX, edição, agendamento, cancelamento e entretenimento — nem responde o \"Acompanhar pedido\".",
  atendimento_humano: "Desligado: a CLEO não manda o aviso de pausa. Ela continua parando de responder quando alguém da equipe assume a conversa.",
};
const clonar = (o) => JSON.parse(JSON.stringify(o));

/**
 * ChatBot → Configurações: quanto de autonomia a CLEO tem nesta loja, por
 * módulo. Mensagem criada pela CLEO = só liga/desliga; mensagem fixa = texto
 * editável + liga/desliga. Salva sozinho; o celular ao lado mostra a prévia.
 */
export default function ConfiguracoesTab({ toast }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(false); // a leitura falhou: antes girava para sempre
  const [aberto, setAberto] = useState("recepcao");
  const [previa, setPrevia] = useState(null);
  const [status, setStatus] = useState("");
  const [loja, setLoja] = useState({ nome: "", logo: "" });
  const timer = useRef(null);
  const apagarStatus = useRef(null);

  const marcar = (s) => {
    setStatus(s);
    clearTimeout(apagarStatus.current);
    if (s === "salvo") apagarStatus.current = setTimeout(() => setStatus(""), 1500);
  };
  const [bot, mudarBot] = useBotSettings({ onStatus: marcar, toast });

  const carregar = useCallback(() => {
    setErro(false);
    cleoApi.config().then((d) => (d?.success ? setDados(d) : setErro(true))).catch(() => setErro(true));
  }, []);

  useEffect(() => {
    carregar();
    fetch(`${API}/api/company/settings`).then((r) => r.json())
      .then((e) => setLoja({ nome: e?.fantasy_name || e?.company_name || "", logo: e?.logo_url || "" })).catch(() => {});
    return () => { clearTimeout(timer.current); clearTimeout(apagarStatus.current); };
  }, [carregar]);

  const mudar = (alterar) => {
    const config = clonar(dados.config);
    alterar(config);
    setDados({ ...dados, config });
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      marcar("salvando");
      try {
        const r = await cleoApi.salvarConfig({
          recepcao: config.recepcao, atendimento: config.atendimento, avisos_pedido: config.avisos_pedido,
          atendimento_humano: config.atendimento_humano, textos: config.textos,
        });
        if (!r?.success) throw new Error(r?.detail || "Não foi possível salvar.");
        marcar("salvo");
      } catch (e) {
        marcar("");
        toast?.({ title: "Erro ao salvar", description: e.message, variant: "destructive" });
      }
    }, 800);
  };

  const cabecalho = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <h2 className="text-xl font-bold">Configurações da CLEO</h2>
        <p className="text-sm text-muted-foreground">Escolha o que a CLEO faz no WhatsApp da loja. Salva sozinho.</p>
      </div>
      {status === "salvando" && <span className="flex items-center gap-1.5 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Salvando…</span>}
      {status === "salvo" && <span className="flex items-center gap-1.5 text-xs text-green-500"><CheckCircle className="h-3 w-3" /> Salvo</span>}
    </div>
  );

  // 1ª abertura (dono, 25/10/2026): título na hora, módulos e celular em cinza.
  // Mesma moldura da tela pronta — a fila de atendimento não recarrega na troca.
  if (!dados) {
    return (
      <div className="p-4 sm:p-6 space-y-4 w-full">
        <FilaAtendimento />
        {cabecalho}
        {erro ? (
          <div className="rounded-xl border p-6 text-center text-sm text-muted-foreground">
            Não foi possível carregar as configurações da CLEO.{" "}
            <button type="button" onClick={carregar} className="font-semibold text-primary hover:underline">Tentar de novo</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-6">
            <CarregandoFormulario campos={5} />
            <CarregandoBloco altura="h-[560px]" className="hidden xl:flex" />
          </div>
        )}
      </div>
    );
  }
  const { config, schema, textos_catalogo: catalogo = {} } = dados;

  const item = (modulo, it) => {
    if (it.id === CHAVE_DO_MODULO[modulo.id]) return null;
    if (it.tipo === "texto") {
      const chave = it.id.replace("texto:", "");
      const spec = catalogo[chave] || {};
      const t = config.textos[chave] || { texto: "", ativa: true };
      return (
        <ItemTexto key={it.id} label={spec.label || chave} hint={spec.hint} variaveis={spec.variaveis} padrao={spec.padrao}
          ligado={t.ativa} onLigar={(v) => mudar((c) => { c.textos[chave].ativa = v; })}
          texto={t.texto} onTexto={(v) => mudar((c) => { c.textos[chave].texto = v; })}
          onVerPrevia={() => setPrevia({ titulo: spec.label || chave, texto: chave === "agradecimento" ? (t.texto || "").split("\n")[0] : t.texto })} />
      );
    }
    if (it.tipo === "numero") {
      return (
        <ItemNumero key={it.id} label={it.label} hint={it.hint} min={it.min} max={it.max} sufixo="horas"
          valor={config[modulo.id][it.id]} onMudar={(v) => mudar((c) => { c[modulo.id][it.id] = Math.min(it.max || v, Math.max(it.min || 0, v)); })} />
      );
    }
    return (
      <ItemIA key={it.id} label={it.label} hint={it.hint} ligado={config[modulo.id][it.id]}
        onLigar={(v) => mudar((c) => { c[modulo.id][it.id] = v; })} />
    );
  };

  const modulo = (m) => {
    const chave = CHAVE_DO_MODULO[m.id];
    return (
      <Modulo key={m.id} icone={ICONES[m.id]} titulo={m.titulo} descricao={m.descricao}
        aberto={aberto === m.id} onToggle={() => setAberto(aberto === m.id ? null : m.id)}
        ligado={chave ? config[m.id]?.[chave] !== false : undefined}
        onLigar={(v) => mudar((c) => { c[m.id] = { ...(c[m.id] || {}), [chave]: v }; })}>
        {chave && config[m.id]?.[chave] === false && DESLIGADO[m.id] && (
          <p className="rounded-lg bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300">{DESLIGADO[m.id]}</p>
        )}
        {m.id === "avisos_pedido" && <ModelosOficiais onVerPrevia={setPrevia} toast={toast} />}
        {m.etapas_do_pedido && <EtapasPedido onVerPrevia={setPrevia} onStatus={marcar} toast={toast} />}
        {m.pausa_atendente && <PausaAtendente bot={bot} mudar={mudarBot} onVerPrevia={setPrevia} />}
        {m.itens.map((it) => item(m, it))}
        {m.id === "avisos_pedido" && <Entretenimento bot={bot} mudar={mudarBot} onVerPrevia={setPrevia} />}
        {m.id === "pos_venda" && (
          <p className="text-xs text-muted-foreground">
            A mensagem de <b>avaliação</b> e o <b>Recuperador de Vendas</b> ficam em Impulsione suas Vendas → Campanhas.
          </p>
        )}
      </Modulo>
    );
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 w-full">
      <FilaAtendimento />
      {cabecalho}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-6">
        <div className="space-y-3 min-w-0">
          {schema.map(modulo)}
        </div>
        <div className="xl:sticky xl:top-4 self-start">
          <PreviaCelular nomeLoja={loja.nome} logo={loja.logo} previa={previa} />
        </div>
      </div>
    </div>
  );
}
